import { useCallback, useEffect, useId, useRef, useState } from 'react';
import Button from './Button';
import './Menu.css';

// A button that opens a short list of actions.
//
// Exists because a header row with five buttons in it stops being a set of
// choices and becomes a wall — the destructive one sits the same size and
// weight as the routine one, and the routine one is the one people want. Two
// labelled menus ("Actions", "Utilities") say what the group IS before you have
// to read the items.
//
// Deliberately a small primitive rather than a full ARIA menu widget: these are
// six-item lists of links and buttons, and the parts that actually matter for
// keyboard and screen-reader users are the ones implemented below — a real
// <button> trigger with aria-expanded, Escape to close, arrow keys to move, and
// focus returning to the trigger on close.

const Chevron = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/**
 * @param {string} label            the trigger's text
 * @param {Array}  items            [{ key, label, onSelect, danger?, description?, disabled? }]
 * @param {string} [variant]        Button variant for the trigger
 * @param {string} [align]          'start' | 'end' — which edge the panel hangs from
 */
export default function Menu({
  label,
  items = [],
  variant = 'secondary',
  size = 'md',
  align = 'end',
  className = '',
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const menuId = useId();

  const close = useCallback(({ refocus = false } = {}) => {
    setOpen(false);
    // Focus goes back to the trigger only when the menu was dismissed by
    // keyboard or by choosing something — not on an outside click, where the
    // user is already somewhere else and yanking focus back is disorienting.
    if (refocus) triggerRef.current?.focus();
  }, []);

  // Close on an outside click and on Escape. Bound only while open, so a page
  // with several of these is not running listeners for all of them at once.
  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (e) => {
      if (!wrapRef.current?.contains(e.target)) close();
    };
    const onKey = (e) => {
      if (e.key === 'Escape') { e.stopPropagation(); close({ refocus: true }); }
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, close]);

  // Move focus into the panel when it opens, so the keyboard path works without
  // a mouse ever being involved.
  useEffect(() => {
    if (!open) return;
    const first = panelRef.current?.querySelector('button:not([disabled])');
    first?.focus();
  }, [open]);

  const onPanelKeyDown = (e) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    e.preventDefault();
    const focusable = [...(panelRef.current?.querySelectorAll('button:not([disabled])') || [])];
    if (!focusable.length) return;
    const i = focusable.indexOf(document.activeElement);
    const next = e.key === 'ArrowDown'
      ? focusable[(i + 1) % focusable.length]
      : focusable[(i - 1 + focusable.length) % focusable.length];
    next.focus();
  };

  const choose = (item) => {
    if (item.disabled) return;
    close({ refocus: true });
    item.onSelect?.();
  };

  return (
    <div className={`ds-menu ${className}`.trim()} ref={wrapRef}>
      <Button
        ref={triggerRef}
        variant={variant}
        size={size}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        iconRight={<Chevron />}
      >
        {label}
      </Button>

      {open && (
        <div
          id={menuId}
          role="menu"
          className={`ds-menu-panel ds-menu-${align}`}
          ref={panelRef}
          onKeyDown={onPanelKeyDown}
        >
          {items.map((item) => (
            <button
              key={item.key || item.label}
              type="button"
              role="menuitem"
              className={`ds-menu-item ${item.danger ? 'is-danger' : ''}`.trim()}
              disabled={item.disabled}
              onClick={() => choose(item)}
            >
              <span className="ds-menu-item-label">{item.label}</span>
              {/* One line of "what this actually opens", because a menu of
                  nouns ("Bills", "Documents") is ambiguous the first time. */}
              {item.description && <span className="ds-menu-item-desc">{item.description}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
